import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
const areas = [{
  num: '01',
  title: 'SALUD EMOCIONAL',
  image: 'https://cdn.magicpatterns.com/uploads/aryiDAzXxfjh2z5bUiYnoD/ChatGPT_Image_18_may_2026,_10_16_38.png',
  desc: 'La ansiedad emocional no solo afecta tu mente. También afecta tu cuerpo, tus decisiones y tu energía.',
  bullets: ['sobrepensamiento', 'agotamiento emocional', 'miedo', 'inseguridad', 'autosabotaje'],
  conclusion: 'Aprender a recuperar claridad emocional cambia completamente la forma en que vives.'
}, {
  num: '02',
  title: 'AMOR Y RELACIONES',
  image: 'https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?w=1200',
  desc: 'Con más de 24 años de matrimonio junto a mi esposa Careliz, entendí que el amor sano no nace desde el apego… sino desde el equilibrio emocional.',
  bullets: ['dependencia emocional', 'rupturas', 'conflictos familiares', 'autoestima emocional', 'relaciones conscientes'],
  conclusion: 'Porque el amor no debería hacerte perder tu paz interior.'
}, {
  num: '03',
  title: 'DINERO, PROPÓSITO Y DIRECCIÓN',
  image: 'https://cdn.magicpatterns.com/uploads/mJrcWYZu3kE9dZgEkf7KYn/ChatGPT_Image_18_may_2026,_10_15_41.png',
  desc: 'Muchas personas quieren crecer… pero emocionalmente viven bloqueadas. Y cuando tu energía está enfocada en el miedo, la ansiedad o el caos emocional, pierdes claridad para avanzar.',
  bullets: ['emprendedores', 'terapeutas', 'creadores de contenido', 'personas que desean construir una marca personal y productos digitales con propósito'],
  conclusion: 'Porque el dinero también responde a tu mentalidad, enfoque y dirección emocional.'
}, {
  num: '04',
  title: 'ESPIRITUALIDAD Y CONEXIÓN CON DIOS',
  image: 'https://cdn.magicpatterns.com/uploads/vaVuUzz78hfHv5mdhFB28A/ChatGPT_Image_18_may_2026,_10_15_50.png',
  desc: 'Hay personas que han puesto relaciones, problemas o heridas emocionales en el lugar que solo Dios debería ocupar. Y ahí comienza el vacío interior.',
  bullets: [],
  conclusion: 'La espiritualidad no es escapar del dolor. Es volver a conectar con tu esencia, tu propósito y con Dios desde una conciencia más profunda.'
}];
const AUTOPLAY_MS = 6000;
export function Areas() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const goTo = (next: number) => {
    setDirection(next > index ? 1 : -1);
    setIndex((next + areas.length) % areas.length);
  };
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);
  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(() => {
      setDirection(1);
      setIndex(i => (i + 1) % areas.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [isPaused, index]);
  const area = areas[index];
  const slideVariants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 60 : -60
    }),
    center: {
      opacity: 1,
      x: 0
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? -60 : 60
    })
  };
  return <section className="py-24 md:py-32 bg-ink relative" id="areas" data-id="element-12">
      <div className="container mx-auto px-6" data-id="element-13">
        <motion.div className="text-center max-w-3xl mx-auto mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} data-id="element-14">
          <span className="text-gold tracking-[0.2em] text-xs md:text-sm font-semibold uppercase mb-4 block" data-id="element-15">
            Las 4 áreas
          </span>
          <h2 className="text-4xl md:text-5xl font-serif text-ivory-bright" data-id="element-16">
            Que más impactan tu vida
          </h2>
          <div className="w-12 h-[1px] bg-gold mx-auto mt-8" data-id="element-17"></div>
        </motion.div>

        <div className="relative max-w-xl mx-auto" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-18">
          {/* Carousel viewport */}
          <div className="relative overflow-hidden" data-id="element-19">
            <div className="relative min-h-[760px] md:min-h-[820px]" data-id="element-20">
              <AnimatePresence mode="wait" custom={direction} data-id="element-21">
                <motion.article key={area.num} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.7,
                ease: [0.16, 1, 0.3, 1]
              }} className="absolute inset-0 bg-ink-light border border-white/5 flex flex-col" data-id="element-22">
                  <div className="relative h-72 md:h-96 overflow-hidden" data-id="element-23">
                    <img src={area.image} alt={area.title} className="w-full h-full object-cover object-top opacity-90" data-id="element-24" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-light to-transparent" data-id="element-25"></div>
                    <div className="absolute bottom-0 left-0 px-8 pt-8 pb-3 flex items-end gap-4 w-full" data-id="element-26">
                      <span className="font-serif text-5xl md:text-6xl text-gold/60 font-light leading-none" data-id="element-27">
                        {area.num}
                      </span>
                      <h3 className="font-serif text-2xl md:text-3xl text-ivory-bright tracking-wide" data-id="element-28">
                        {area.title}
                      </h3>
                    </div>
                  </div>

                  <div className="p-8 md:p-10 flex-grow flex flex-col" data-id="element-29">
                    <p className="text-muted font-light leading-relaxed mb-6 text-base md:text-lg" data-id="element-30">
                      {area.desc}
                    </p>

                    {area.bullets.length > 0 && <ul className="space-y-3 mb-8 flex-grow" data-id="element-31">
                        {area.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3 text-ivory/80 font-light text-sm md:text-base" data-id="element-32">
                            <span className="text-gold mt-1.5 text-[10px]" data-id="element-33">
                              ♦
                            </span>
                            <span data-id="element-34">{bullet}</span>
                          </li>)}
                      </ul>}

                    <div className="mt-auto pt-6 border-t border-white/5" data-id="element-35">
                      <p className="font-serif text-lg md:text-xl text-ivory italic" data-id="element-36">
                        {area.conclusion}
                      </p>
                    </div>
                  </div>
                </motion.article>
              </AnimatePresence>
            </div>
          </div>

          {/* Arrow controls */}
          <button onClick={prev} aria-label="Área anterior" className="absolute left-0 -translate-x-1/2 md:-translate-x-full top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-ink border border-gold/30 text-gold hover:bg-gold hover:text-ink transition-colors duration-300" data-id="element-37">
            <ChevronLeft className="w-5 h-5" data-id="element-38" />
          </button>
          <button onClick={next} aria-label="Siguiente área" className="absolute right-0 translate-x-1/2 md:translate-x-full top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-ink border border-gold/30 text-gold hover:bg-gold hover:text-ink transition-colors duration-300" data-id="element-39">
            <ChevronRight className="w-5 h-5" data-id="element-40" />
          </button>

          {/* Indicators */}
          <div className="flex items-center justify-center gap-3 mt-10" data-id="element-41">
            {areas.map((a, i) => <button key={a.num} onClick={() => goTo(i)} aria-label={`Ir al área ${a.num}`} className="group relative h-[2px] w-12 bg-white/10 overflow-hidden" data-id="element-42">
                <motion.span className="absolute inset-0 bg-gold origin-left" initial={false} animate={{
              scaleX: i === index ? 1 : i < index ? 1 : 0
            }} transition={{
              duration: i === index && !isPaused ? AUTOPLAY_MS / 1000 : 0.4,
              ease: i === index && !isPaused ? 'linear' : 'easeOut'
            }} key={`${i}-${index}-${isPaused}`} data-id="element-43" />
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}